import DatePicker from "../../../components/DatePicker"
import { FormikProps } from "formik"
import { HourReportFormState } from "../../schemas"
import { ChevronLeft, ChevronRight } from "react-feather"
import { useLocalizedFormat } from "../../../hooks/use-localized-format"
import { Box, Button, Typography, makeStyles, Theme } from "@material-ui/core"
import { useTranslation } from "react-i18next"

export const DayPicker = ({ formik, onDateChange }: DayPickerProps) => {
  const classes = useStyles()
  const { values, setFieldValue } = formik
  const format = useLocalizedFormat()
  const { t } = useTranslation("hour-report")

  const setDate = (date: Date) => {
    setFieldValue("date", format(date, "yyyy-MM-dd"))
    onDateChange?.(date)
  }

  const selectedDate = new Date(values.date)
  const prevDay = new Date(selectedDate)
  prevDay.setDate(selectedDate.getDate() - 1)
  const formattedPrevDay = format(prevDay, "MMM dd")
  const nextDay = new Date(selectedDate)
  nextDay.setDate(selectedDate.getDate() + 1)
  const formattedNextDay = format(nextDay, "MMM dd")

  const changeDate = (offset: number) => {
    const newDate = new Date(selectedDate)
    newDate.setDate(newDate.getDate() + offset)
    setDate(newDate)
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {t("form.date", { defaultValue: "Date" })}
      </Typography>
      <Box display="flex" alignItems="center" justifyContent={"space-between"} marginBottom={2}>
        <Box mr={2}>
          <Button
            variant="outlined"
            onClick={() => changeDate(-1)}
            startIcon={<ChevronLeft size={18} />}
            style={{ padding: 6 }}
            fullWidth
          >
            {formattedPrevDay}
          </Button>
        </Box>

        <Box flex={1} mr={2}>
          <DatePicker
            onChange={(date: Date | null) => date && setDate(date)}
            selected={selectedDate}
            isClearable={false}
            dateFormat={"MMM dd, yyyy"}
            textFieldProps={{
              size: "small",
              className: classes.datePickerTextField,
              fullWidth: true,
              inputProps: {
                className: classes.datePickerInput,
              },
            }}
          />
        </Box>

        <Box>
          <Button
            variant="outlined"
            onClick={() => changeDate(1)}
            endIcon={<ChevronRight size={18} />}
            style={{ padding: 6 }}
            fullWidth
          >
            {formattedNextDay}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  datePickerInput: {
    fontSize: theme.typography.button.fontSize,
    fontWeight: theme.typography.button.fontWeight,
    letterSpacing: theme.typography.button.letterSpacing,
    lineHeight: theme.typography.button.lineHeight,
    textAlign: "center",
    paddingTop: 11,
    paddingBottom: 11,
  },
  datePickerTextField: {
    "& svg": {
      height: 18,
    },
  },
}))

type DayPickerProps = {
  formik: FormikProps<HourReportFormState>
  onDateChange?: (date: Date) => void
}
